import { ColumnDef } from '@tanstack/react-table';
import { Download, Settings2 } from 'lucide-react';
import { Fragment, useCallback, useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/DataTable';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { badgeClasses, convertISODateToMMDDYYYY, formatCurrency } from '@/lib/utils';
import { hasFeature } from '@/services/featureService';
import { exportPaymentHistoryCsv, exportPaymentHistoryPdf, usePaymentHistory } from '@/services/paymentHistoryService';
import type {
  InvoiceTypeFilter,
  PaymentHistoryItem,
  PaymentHistoryQueryParams,
  PaymentHistoryTab,
  PaymentTypeFilter,
} from '@/types/paymentHistory.types';
import { Feature } from '@/utils/constant';

const TABS: { value: PaymentHistoryTab; label: string }[] = [
  { value: 'GLOBAL', label: 'Global' },
  { value: 'AR', label: 'Accounts Receivable' },
  { value: 'VENDOR', label: 'Vendors Payment' },
  { value: 'WITNESS', label: 'Witness Fees' },
];

const INVOICE_TYPE_OPTIONS: { value: InvoiceTypeFilter; label: string }[] = [
  { value: 'PREPAID', label: 'Only Pre-paid' },
  { value: 'ALL', label: 'All' },
];

const PAYMENT_TYPE_OPTIONS: { value: PaymentTypeFilter; label: string }[] = [
  { value: 'CHECK_OR_ECHECK', label: 'Check / E-check' },
  { value: 'CARD', label: 'Credit Card' },
  { value: 'ACH', label: 'ACH Transfers' },
  { value: 'WIRE', label: 'Wire Transfers' },
];

const PaymentHistory = () => {
  const [activeTab, setActiveTab] = useState<PaymentHistoryTab>('GLOBAL');
  const [invoiceType, setInvoiceType] = useState<InvoiceTypeFilter>('ALL');
  const [paymentType, setPaymentType] = useState<PaymentTypeFilter | undefined>(undefined);
  const [isExporting, setIsExporting] = useState(false);

  const showPaymentTypeFilter = activeTab === 'GLOBAL' || activeTab === 'AR';
  const showInvoiceTypeFilter = activeTab !== 'WITNESS';
  const canExport = hasFeature(Feature.PAYMENT_HISTORY_EXPORT);

  const handleTabChange = useCallback((tab: PaymentHistoryTab) => {
    setActiveTab(tab);
    setInvoiceType('ALL');
    setPaymentType(undefined);
  }, []);

  const dataTableFilters = useMemo(
    () => ({
      tab: activeTab,
      ...(invoiceType !== 'ALL' ? { invoiceType } : {}),
      ...(showPaymentTypeFilter && paymentType ? { paymentType } : {}),
    }),
    [activeTab, invoiceType, paymentType, showPaymentTypeFilter]
  );

  const apiGetWrapper = useCallback((params: Record<string, unknown>) => {
    const apiParams: PaymentHistoryQueryParams = {
      ...params,
      tab: params.tab as PaymentHistoryTab,
      invoiceType: params.invoiceType as InvoiceTypeFilter | undefined,
      paymentType: params.paymentType as PaymentTypeFilter | undefined,
      pageNo: params.pageNo as number,
      pageSize: params.pageSize as number,
      sortField: params.sortField as PaymentHistoryQueryParams['sortField'],
      sortDirection: params.sortDirection as PaymentHistoryQueryParams['sortDirection'],
    };
    return usePaymentHistory(apiParams);
  }, []);

  const currentExportParams = useMemo<PaymentHistoryQueryParams>(
    () => ({
      tab: activeTab,
      invoiceType: invoiceType !== 'ALL' ? invoiceType : undefined,
      paymentType: showPaymentTypeFilter ? paymentType : undefined,
    }),
    [activeTab, invoiceType, paymentType, showPaymentTypeFilter]
  );

  const handleExportCsv = useCallback(async () => {
    setIsExporting(true);
    try {
      await exportPaymentHistoryCsv(currentExportParams);
    } finally {
      setIsExporting(false);
    }
  }, [currentExportParams]);

  const handleExportPdf = useCallback(async () => {
    setIsExporting(true);
    try {
      await exportPaymentHistoryPdf(currentExportParams);
    } finally {
      setIsExporting(false);
    }
  }, [currentExportParams]);

  const columns: ColumnDef<PaymentHistoryItem>[] = useMemo(
    () => [
      {
        accessorKey: 'dueDate',
        header: 'Due Date',
        enableSorting: true,
        cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          return (
            <div className="flex w-full min-w-[100px] mr-5">
              <span>{value ? convertISODateToMMDDYYYY(value) : '-'}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'executedDate',
        header: 'Executed Date',
        enableSorting: true,
        cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          return (
            <div className="flex w-full min-w-[100px] mr-5">
              <span>{value ? convertISODateToMMDDYYYY(value) : '-'}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'invoiceNumber',
        header: 'Invoice #',
        enableSorting: true,
        cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          return (
            <div className="flex w-full min-w-[100px] mr-5">
              <span>{value ?? '-'}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'clientId',
        header: 'Client #',
        enableSorting: true,
        cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          return (
            <div className="flex w-full min-w-[80px] mr-5">
              <span>{value ?? '-'}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'docketNumber',
        header: 'Docket #',
        enableSorting: true,
        cell: ({ cell }) => {
          const value = cell.getValue() as string | null;
          return (
            <div className="flex w-full min-w-[120px] mr-5">
              <span>{value ?? '-'}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'amount',
        header: 'Amount',
        enableSorting: true,
        cell: ({ cell }) => {
          const value = cell.getValue() as number;
          return (
            <div className="flex w-full min-w-[80px] mr-5">
              <span>{formatCurrency(value ?? 0)}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'transactionStatus',
        header: 'Transaction Status',
        enableSorting: false,
        cell: () => <Badge className={`mr-5 ${badgeClasses} bg-[#D5F4E3] text-[#1B8652]`}>Successful</Badge>,
      },
    ],
    []
  );

  return (
    <div className="flex w-full flex-col gap-6">
      <div
        className="flex flex-col sm:flex-row sm:justify-between w-full sm:items-center gap-3 sm:gap-0"
        id="payment-history-header"
      >
        <h1 className="text-xl sm:text-2xl font-semibold">Payment History</h1>
        <div className="flex flex-row gap-2 sm:gap-4 self-end sm:self-auto">
          <DropdownMenu>
            <DropdownMenuTrigger
              asChild
              className="data-[state=open]:bg-[#EDF5FF] bg-white !border-[0px] min-w-[87px] w-fit h-[36px] !text-primary font-[500] text-sm"
            >
              <Button
                className="flex flex-row gap-2 !focus:bg-[#EDF5FF] align-middle border-1"
                variant="outline"
                data-testid="view-filters-button"
              >
                <span>View</span>
                <Settings2 className="w-4 h-4" color="#0A2240" strokeWidth="1px" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56 mr-8">
              {showInvoiceTypeFilter && (
                <Fragment>
                  <DropdownMenuLabel className="pl-8 text-sm font-[500]">Invoice Type</DropdownMenuLabel>
                  {INVOICE_TYPE_OPTIONS.map((option) => (
                    <DropdownMenuCheckboxItem
                      key={option.value}
                      className={`text-placeholder ${
                        invoiceType === option.value ? 'text-primary bg-[#EDF5FF] font-[500]' : ''
                      }`}
                      checked={invoiceType === option.value}
                      onCheckedChange={() => setInvoiceType(option.value)}
                    >
                      {option.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </Fragment>
              )}
              {showPaymentTypeFilter && (
                <Fragment>
                  {showInvoiceTypeFilter && <DropdownMenuSeparator />}
                  <DropdownMenuLabel className="pl-8 text-sm font-[500]">Payment Type</DropdownMenuLabel>
                  {PAYMENT_TYPE_OPTIONS.map((option) => (
                    <DropdownMenuCheckboxItem
                      key={option.value}
                      className={`text-placeholder ${
                        paymentType === option.value ? 'text-primary bg-[#EDF5FF] font-[500]' : ''
                      }`}
                      checked={paymentType === option.value}
                      onCheckedChange={() =>
                        setPaymentType((prev) => (prev === option.value ? undefined : option.value))
                      }
                    >
                      {option.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </Fragment>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {canExport && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="flex flex-row gap-2 align-middle border-1 min-w-[87px] h-[36px] !text-primary font-[500] text-sm bg-white"
                  data-testid="export-button"
                  disabled={isExporting}
                >
                  <span>Export</span>
                  <Download className="w-4 h-4" color="#0A2240" strokeWidth="1.5px" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[160px] mr-8">
                <DropdownMenuItem onClick={handleExportCsv} disabled={isExporting} data-testid="export-csv">
                  Export CSV
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleExportPdf}
                  disabled={isExporting}
                  data-testid="export-pdf"
                  className="text-primary bg-[#EDF5FF] font-[500]"
                >
                  Export PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div
        className="flex flex-row w-full border-b border-[#E2E8F0] overflow-x-auto scrollbar-hide"
        role="tablist"
        aria-label="Payment History tabs"
      >
        {TABS.map((tab) => {
          const isActive = activeTab === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => handleTabChange(tab.value)}
              className={`flex-1 sm:flex-1 min-w-[110px] px-3 sm:px-6 py-3 text-xs sm:text-sm font-medium text-center whitespace-nowrap border-b-2 transition-colors -mb-px ${
                isActive
                  ? 'border-primary text-primary border-b-[3px]'
                  : 'border-transparent text-[#64748B] hover:text-primary'
              }`}
              data-testid={`tab-${tab.value.toLowerCase()}`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="w-full overflow-x-auto">
        <DataTable columns={columns} apiGet={apiGetWrapper} filters={dataTableFilters} key={activeTab} />
      </div>
    </div>
  );
};

export default PaymentHistory;
